import React from 'react';

// Assets
import emptyEnterpriseModelsImage from '@/images/design-system-v2/empty-state/emptyModels.png';

// Enums
import { PORTFOLIO_METRICS_FIELDS, PORTFOLIO_TABLE_COLUMNS } from '@/enums/Portfolio';

// Components
import EmptyStateCard from '@/views/components/misc/EmptyStateCard';
import PortfoliosTableV2 from '@/views/components/tables/PortfoliosTableV2';
import { LoadingSpinner } from '@/views/components/common';
import { SearchInput } from '@/views/components/inputs';
import { Typography } from '@capintel/component-lib/typography';

// Legacy Component
import LegacyEnterpriseModelsTab from '@/views/containers/models/LegacyEnterpriseModels';

// Hooks
import useThemeVersion from '@/hooks/useThemeVersion';

// Libraries
import classNames from 'classnames';
import { SortingRule } from 'react-table';

// Types
import EnterpriseDetails from '@/types/enterprise-detail-info';
import Portfolio from '@/types/portfolio';
import User from '@/types/user';
import { ALLOWED_HTML_TAGS } from '@/views/components/i18n/FormattedMessageV2';

// Utils
import { formatMessage } from '@/utils/i18n';
import { getPortfolioMissingOrInvalidHoldings } from '@/utils/portfolio/utils';
import { userHasPermissionEnabled, PERMISSIONS, userHasPermission } from '@/utils/permissions';

type Props = {
  user: User;
  query?: string;
  searching: boolean;
  initialized: boolean;
  models: Portfolio[];
  enterprise: EnterpriseDetails;
  comparingModelIds: string[];
  hasMore: boolean;
  onSearch: (query: string) => void;
  onLoadMore: () => void;
  onDeletePortfolio: (portfolios: Portfolio[]) => void;
  onEditPortfolio: (portfolio: Portfolio) => void;
  onClonePortfolio?: (portfolio: Portfolio) => void;
  onCompareSelected: (portfolios: Portfolio[]) => void;
};

type LegacyProps = {
  onSort: (sorting: SortingRule[]) => void;
  onCompare: (portfolio: Portfolio) => void;
};

type WrapperProps = LegacyProps & Props;

class EnterpriseModelsTabV2 extends React.Component<WrapperProps> {
  containerRef = React.createRef<HTMLDivElement>();

  private i18nEmptyStatePath = 'EmptyStateCard.EnterpriseModels';

  getDisplayedColumns = () => {
    const { user } = this.props;

    return [
      {
        key: PORTFOLIO_TABLE_COLUMNS.NAME,
      },
      {
        key: PORTFOLIO_TABLE_COLUMNS.LOCK,
      },
      {
        key: PORTFOLIO_TABLE_COLUMNS.RISK_PROFILE,
      },
      {
        key: PORTFOLIO_TABLE_COLUMNS.LAST_MODIFIED,
      },
      {
        key: PORTFOLIO_TABLE_COLUMNS.SHOW_DETAILS,
        hidden: !userHasPermissionEnabled(user, PERMISSIONS.SHOW_PORTFOLIO_DETAILS),
      },
      {
        key: PORTFOLIO_TABLE_COLUMNS.COMPARE,
      },
      {
        key: PORTFOLIO_TABLE_COLUMNS.MENU_TOGGLE,
      },
    ]
      .filter(col => !col.hidden)
      .map(col => col.key);
  };

  renderPortfoliosSection = (title: string, portfolios: Portfolio[], enterprise?: EnterpriseDetails) => {
    const {
      user,
      comparingModelIds,
      hasMore,
      initialized,
      onCompareSelected,
      onClonePortfolio,
      onDeletePortfolio,
      onEditPortfolio,
      onLoadMore,
      onSearch,
      query,
      searching,
    } = this.props;

    const displayedColumns = this.getDisplayedColumns();
    const displayedMetrics = [PORTFOLIO_METRICS_FIELDS.MER, PORTFOLIO_METRICS_FIELDS.YEAR_ONE, PORTFOLIO_METRICS_FIELDS.YEAR_THREE];
    const showEmptyCard = portfolios.length === 0 && !query && !searching;

    const getEmptyStateCard = (): JSX.Element => (
      <EmptyStateCard
        imageAlt="Enterprise models placeholder image"
        imagePath={emptyEnterpriseModelsImage}
        messagePath={`${this.i18nEmptyStatePath}.Message`}
        titlePath={`${this.i18nEmptyStatePath}.Title`}
      />
    );

    return (
      <>
        {showEmptyCard ? (
          getEmptyStateCard()
        ) : (
          <>
            <div className="models-content-header">
              <Typography as={ALLOWED_HTML_TAGS.H2} variant="headerSm">
                {title}
              </Typography>
              <div className="models-content-header-actions">
                <SearchInput
                  className="search-input-v2"
                  key="models"
                  loading={searching}
                  onChange={onSearch}
                  placeholder={formatMessage('Models.SearchEnterpriseModels')}
                />
              </div>
            </div>

            <div className="models-content-portfolios">
              {(searching || !initialized) && (
                <div className="models-content-loading-container">
                  <LoadingSpinner />
                </div>
              )}

              <PortfoliosTableV2<Portfolio>
                className={classNames({ 'visually-hidden': searching || !initialized })}
                comparingPortfolioIds={comparingModelIds}
                displayedColumns={displayedColumns}
                displayedMetrics={displayedMetrics}
                hasMore={hasMore}
                locked={!!enterprise}
                onCompare={onCompareSelected}
                onDelete={onDeletePortfolio}
                onEdit={onEditPortfolio}
                {...(userHasPermission(user, PERMISSIONS.USER_MODELS_CREATE) ? { onCopy: onClonePortfolio } : {})}
                onLoadMore={onLoadMore}
                portfolios={portfolios}
                query={query}
                selectedTitle="PortfoliosTable.SelectedTitle.Models"
                messageIds={{
                  deleteMultiple: 'PortfoliosTable.Buttons.DeleteSelectedModels',
                  deleteSingle: 'PortfoliosTable.Buttons.DeleteModel',
                  editSingle: 'PortfoliosTable.Buttons.EditModel',
                  copySingle: 'PortfoliosTable.Buttons.CopyModel',
                }}
                missingProductPredicate={portfolio => getPortfolioMissingOrInvalidHoldings(portfolio).length > 0}
                canClonePredicate={portfolio => !portfolio.availableActions || portfolio.availableActions.clone}
              />
            </div>
          </>
        )}
      </>
    );
  };

  render() {
    const { enterprise, models } = this.props;
    const title = enterprise && formatMessage('Models.EnterpriseTitle', { name: enterprise.name, count: enterprise.name.endsWith('s') ? 1 : 0 });

    return (
      <div ref={this.containerRef} className="models-content">
        {enterprise && models && title && this.renderPortfoliosSection(title, models, enterprise)}
      </div>
    );
  }
}

const EnterpriseModelsTab = (props: WrapperProps) => {
  const { isLightVersion } = useThemeVersion();
  if (isLightVersion) {
    return <EnterpriseModelsTabV2 {...props} />;
  }
  return <LegacyEnterpriseModelsTab {...props} />;
};

export default EnterpriseModelsTab;
